import React, { useState } from 'react';
import { mockQuestions } from '../data/mockCommunity';
import QuestionCard from '../components/community/QuestionCard';
import RoadmapBoard from '../components/community/RoadmapBoard';
import RecognitionBoard from '../components/community/RecognitionBoard';
import SafeIcon from '../components/common/SafeIcon';
import TabIndicator from '../components/common/TabIndicator';

const faqCategories = ['Todos', 'Ventas', 'Educación Financiera', 'Mindset', 'Onboarding'];

export default function CommunityPage() {
  // 'faq' | 'roadmap' | 'reconocimientos'
  const [activeMainTab, setActiveMainTab] = useState('faq'); 
  
  // FAQ States
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [search, setSearch] = useState('');

  const filteredQuestions = mockQuestions.filter(q => {
    const matchesCategory = activeCategory === 'Todos' || q.category === activeCategory;
    const matchesSearch = q.title.toLowerCase().includes(search.toLowerCase()) || 
                          q.description.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[var(--bg-base)] transition-colors duration-300">
      {/* Header Section */}
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-subtle)] pt-12 pb-0 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 text-center md:text-left">
            <h1 className="text-4xl md:text-6xl font-black text-[var(--text-main)] tracking-tighter uppercase italic leading-none mb-4">
              Nuestra <span className="gfi-gradient-text">Comunidad</span>
            </h1>
            <p className="text-[var(--text-secondary)] font-medium max-w-xl uppercase text-xs tracking-[0.2em] md:mx-0 mx-auto">
              El conocimiento centralizado y las ideas que construyen el futuro de GFI Latino.
            </p>
          </div>

          {/* Main Tabs */}
          <div className="flex gap-8 overflow-x-auto hide-scrollbar border-b border-[var(--border-subtle)]">
            <button
              onClick={() => setActiveMainTab('faq')}
              className={`pb-4 flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] transition-all relative whitespace-nowrap ${
                activeMainTab === 'faq' ? 'text-[var(--text-main)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
              }`}
            >
              <SafeIcon name="MessageCircle" className="w-4 h-4" />
              FAQ Interno
              {activeMainTab === 'faq' && <TabIndicator />}
            </button>
            <button
              onClick={() => setActiveMainTab('roadmap')}
              className={`pb-4 flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] transition-all relative whitespace-nowrap ${
                activeMainTab === 'roadmap' ? 'text-[var(--text-main)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
              }`}
            >
              <SafeIcon name="Map" className="w-4 h-4" />
              Roadmap de Ideas
              {activeMainTab === 'roadmap' && <TabIndicator />}
            </button>
            <button
              onClick={() => setActiveMainTab('reconocimientos')}
              className={`pb-4 flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] transition-all relative whitespace-nowrap ${
                activeMainTab === 'reconocimientos' ? 'text-[var(--text-main)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
              }`}
            >
              <SafeIcon name="Award" className="w-4 h-4" />
              Reconocimientos
              {activeMainTab === 'reconocimientos' && <TabIndicator />}
            </button>
          </div>
        </div>
      </div>

      {/* Content Rendering based on Tab */}
      <div className="pb-24">
        {activeMainTab === 'faq' && (
          <div className="max-w-5xl mx-auto px-6 py-8 animate-fade-in">
            {/* Action Bar (Search + Add Question) */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <input 
                  type="text" 
                  placeholder="BUSCAR PREGUNTAS O PALABRAS CLAVE..." 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-4 px-6 pr-12 text-sm font-bold text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan transition-all shadow-sm"
                />
                <SafeIcon name="Search" className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
              </div>
              <button className="gfi-gradient-bg text-brand-navy px-8 py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 md:w-auto w-full shrink-0">
                <SafeIcon name="Plus" className="w-4 h-4" />
                Haz una Pregunta
              </button>
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-4 mb-4">
              {faqCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex-none px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${
                    activeCategory === cat 
                      ? 'bg-[var(--text-main)] text-[var(--bg-base)] border-[var(--text-main)] shadow-md' 
                      : 'bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--text-main)]/30'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* List of Questions */}
            {filteredQuestions.length > 0 ? (
              <div className="space-y-4">
                {filteredQuestions.map(question => (
                  <QuestionCard key={question.id} question={question} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center bg-[var(--bg-surface)] rounded-3xl border border-[var(--border-subtle)]">
                <div className="w-20 h-20 rounded-full bg-[var(--bg-base)] flex items-center justify-center mb-6">
                  <SafeIcon name="MessageSquare" className="w-8 h-8 text-[var(--border-subtle)]" />
                </div>
                <h3 className="text-xl font-black text-[var(--text-main)] uppercase italic mb-2">No hay resultados</h3>
                <p className="text-[var(--text-secondary)] text-sm mb-6 max-w-sm">
                  No encontramos preguntas que coincidan con tu búsqueda. ¡Sé el primero en hacerla!
                </p>
                <button className="text-brand-cyan text-xs font-black uppercase tracking-widest hover:underline">
                  Crear nueva pregunta
                </button>
              </div>
            )}
          </div>
        )}

        {activeMainTab === 'roadmap' && (
          <div className="animate-fade-in pt-4">
            <RoadmapBoard />
          </div>
        )}

        {activeMainTab === 'reconocimientos' && (
          <RecognitionBoard />
        )}
      </div>
    </div>
  );
}